import { z } from 'zod';
import { esquemaMotivo } from './comunes.js';
/**
 * Parametros del electrocardiograma (F3.5, CARD-01, plan maestro §7.2).
 *
 * Todos son opcionales: el original (PDF o imagen) es lo que vale, y los
 * parametros solo ayudan a comparar. Tres reglas que no se negocian:
 *
 * - El QTc SIEMPRE lleva su metodo. Un «QTc 450» sin formula no se puede
 *   comparar con otro, porque Bazett y Fridericia dan cifras distintas para el
 *   mismo trazo.
 * - Se distingue de donde salen: `transcrito` (lo midio o leyo el profesional en
 *   el trazo) o `importado` (lo imprimio el equipo en su informe). La
 *   interpretacion del profesional va aparte, en el resultado del estudio: lo que
 *   dice el equipo no es una interpretacion.
 * - No hay OCR ni regla de medicion sobre la imagen: una captura sin calibracion
 *   validada no tiene escala fiable (§7.2, «Avanzado»).
 */
export const ORIGENES_DE_PARAMETROS_ECG = ['transcrito', 'importado'];
export const NOMBRE_DE_ORIGEN_ECG = {
    transcrito: 'Medido o leído por el profesional',
    importado: 'Impreso por el equipo',
};
export const METODOS_QTC = ['bazett', 'fridericia', 'framingham', 'hodges'];
export const NOMBRE_DE_METODO_QTC = {
    bazett: 'Bazett',
    fridericia: 'Fridericia',
    framingham: 'Framingham',
    hodges: 'Hodges',
};
/** Version de las formulas de `calcularQtc`. Viaja con cada QTc calculado. */
export const VERSION_FORMULAS_QTC = 1;
/**
 * QTc en milisegundos a partir del QT (ms) y la frecuencia (lpm), con RR en
 * segundos = 60 / frecuencia. Redondeado al milisegundo.
 *
 * - Bazett: QT / RR^(1/2)
 * - Fridericia: QT / RR^(1/3)
 * - Framingham: QT + 154 · (1 − RR)
 * - Hodges: QT + 1.75 · (frecuencia − 60)
 */
export function calcularQtc(qtMs, frecuenciaLpm, metodo) {
    const rr = 60 / frecuenciaLpm;
    switch (metodo) {
        case 'bazett':
            return Math.round(qtMs / Math.sqrt(rr));
        case 'fridericia':
            return Math.round(qtMs / Math.cbrt(rr));
        case 'framingham':
            return Math.round(qtMs + 154 * (1 - rr));
        case 'hodges':
            return Math.round(qtMs + 1.75 * (frecuenciaLpm - 60));
    }
}
const intervaloMs = z.number().int().min(20).max(1000);
const eje = z.number().int().min(-180).max(180);
export const esquemaQtc = z
    .object({
    metodo: z.enum(METODOS_QTC),
    /**
     * `calculado`: lo calcula el servidor con QT y frecuencia; `reportado`: el
     * valor viene del informe (con el metodo que dice el informe).
     */
    procedencia: z.enum(['calculado', 'reportado']),
    valorMs: z.number().int().min(150).max(900).optional(),
})
    .strict();
export const esquemaGuardarParametrosEcg = z
    .object({
    versionEsperada: z.number().int().min(0),
    origen: z.enum(ORIGENES_DE_PARAMETROS_ECG),
    frecuenciaLpm: z.number().int().min(15).max(350).optional(),
    ritmo: z.string().trim().max(120).optional(),
    prMs: intervaloMs.optional(),
    qrsMs: intervaloMs.optional(),
    qtMs: intervaloMs.optional(),
    qtc: esquemaQtc.optional(),
    ejes: z
        .object({ p: eje.optional(), qrs: eje.optional(), t: eje.optional() })
        .strict()
        .optional(),
    /** Lo que concluye el informe del equipo. NO es la interpretacion del profesional. */
    conclusionesDelEquipo: z.string().trim().max(1000).optional(),
    /** Obligatorio si el resultado ya era definitivo: el cambio lo corrige. */
    motivo: esquemaMotivo.optional(),
})
    .strict()
    .superRefine((datos, ctx) => {
    if (datos.qtc?.procedencia === 'calculado' && (!datos.qtMs || !datos.frecuenciaLpm)) {
        ctx.addIssue({
            code: 'custom',
            message: 'Para calcular el QTc hacen falta el QT y la frecuencia',
            path: ['qtc'],
        });
    }
    if (datos.qtc?.procedencia === 'reportado' && datos.qtc.valorMs === undefined) {
        ctx.addIssue({
            code: 'custom',
            message: 'Escriba el QTc que reporta el informe',
            path: ['qtc', 'valorMs'],
        });
    }
    if (datos.qtc?.procedencia === 'calculado' && datos.qtc.valorMs !== undefined) {
        ctx.addIssue({
            code: 'custom',
            message: 'Un QTc calculado lo calcula el sistema: no se escribe',
            path: ['qtc', 'valorMs'],
        });
    }
});
/** Firma el reporte de la version vigente del estudio. */
export const esquemaFirmarReporte = z.object({ versionEsperada: z.number().int().min(0) }).strict();
export const esquemaConfirmarCorrespondencia = z
    .object({ versionEsperada: z.number().int().min(0) })
    .strict();
//# sourceMappingURL=ecg.js.map